import React, {ReactElement, useCallback, useMemo, useState} from 'react'
import {api} from "./api";
import {Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle} from "@mui/material";
import {useTranslation} from "react-i18next";

export type inputProps = {
    url: string | null
}

export type outputProps = {
    Dialog: () => ReactElement
    call: (extension: string, continueFunction: () => Promise<void>) => Promise<void>
}

type CheckResult = {
    empty: boolean,
    users: string[] | null
    ivrs: string[] | null
    huntgroups: string[] | null
    queues: string[] | null
    premiumCallCenters: string[] | null
}

const useExtensionUpdateChecker = ({url} : inputProps) : outputProps => {
    const { t } = useTranslation()
    const [checkResult, setCheckResult] = useState<CheckResult | null>(null)
    const [contFunction, setContFunction] = useState<(() => Promise<void>) | null>(null)

    const doCall = useCallback(async (extension: string, continueFunction: () => Promise<void>) => {
            setContFunction(() => continueFunction);

            const { data } = await api.get<CheckResult>(url + '?extension=' + extension)
            if (data.empty) {
                await continueFunction()
            } else {
                setCheckResult(data)
            }
        },
        [url, setContFunction]
    )

    const CheckDialog = useMemo(() =>
        checkResult !== null ? <Dialog open={true} onClose={() => setCheckResult(null)} aria-labelledby='alert-dialog-title'
                    aria-describedby='alert-dialog-description'>
                <DialogTitle id='warning-dialog-title'>{t('general.warning')}</DialogTitle>
                <DialogContent>
                    <DialogContentText id='alert-dialog-description'>
                        {t('general.warnings.extension-update.title')}
                        <ul>
                            {checkResult.users?.length? <li>{t('users.title')}: {checkResult.users?.join(', ')}</li> : <></>}
                            {checkResult.ivrs?.length? <li>{t('ivrs.title')}: {checkResult.ivrs?.join(', ')}</li> : <></>}
                            {checkResult.queues?.length? <li>{t('enterprise-sites.table.queues')}: {checkResult.queues?.join(', ')}</li> : <></>}
                            {checkResult.huntgroups?.length? <li>{t('enterprise-sites.table.hunt-groups')}: {checkResult.huntgroups?.join(', ')}</li> : <></>}
                            {checkResult.premiumCallCenters?.length? <li>{t('ivrs.title')}: {checkResult.premiumCallCenters?.join(', ')}</li> : <></>}
                        </ul>
                        {t('general.warnings.extension-update.continue-question')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button autoFocus onClick={() => setCheckResult(null)}>
                        {t('general.cancel')}
                    </Button>
                    <Button variant='contained' onClick={async () => {if (contFunction != null) { setCheckResult(null); await contFunction(); }}} >
                        {t('general.ok')}
                    </Button>
                </DialogActions>
            </Dialog> : <></>
        , [checkResult, contFunction, t])
    return {
        Dialog: () => CheckDialog,
        call: doCall
    }
}

export default useExtensionUpdateChecker